'use client';
import React, { useEffect, useState } from 'react';
import { animate, useMotionValue } from 'motion/react';
import { getBarHeight } from '../utils/get-bar-height';
import Image from 'next/image';
import SavingsInfo from './savings-info';
import { useIsMobile } from '@/app/hooks/use-is-mobile';
import { PriceType } from '../types/price-type';
import useOfferComparisonStore, {
  priceSelector,
  selectSavingsAmount,
  selectTotalEnergyCostsInclPromo,
} from '../store/use-offer-comparison-store';
import animationConfig from '@/lib/config/animation-config';
import { PriceAmount, priceBar, TariffName } from './comparison-bar-data';
import { ReferenceTariffType } from '@/app/types/reference-tariff-type';
import { useCustomizationContext } from '../customizations-provider';

interface ComparisonBarsProps {
  includeGridCosts: boolean;
  isMonthlyRate: boolean;
}

export default function ComparisonBars({
  includeGridCosts,
  isMonthlyRate,
}: ComparisonBarsProps) {
  const { initial, accelerated } = animationConfig.speeds;
  const [includeDecimals, setIncludeDecimals] = useState(true);
  const c = useCustomizationContext();
  const logo = c('tariff.logo');
  const { currentTariffCost, averageCost, mode } = useOfferComparisonStore();
  const gridCost = useOfferComparisonStore((state) =>
    priceSelector(state, state.gridCost)
  );
  const hasInvoice = mode === ReferenceTariffType.invoice;

  const offerPrice =
    useOfferComparisonStore(selectTotalEnergyCostsInclPromo) ?? 0;

  const tariffPrice = useOfferComparisonStore((state) =>
    priceSelector(
      state,
      (mode === ReferenceTariffType.invoice
        ? currentTariffCost
        : averageCost) ?? 0
    )
  );
  const savings = useOfferComparisonStore(selectSavingsAmount);

  const calculatePrice = (price: number) => {
    let finalPrice = price;
    if (includeGridCosts) finalPrice += gridCost;
    if (isMonthlyRate) finalPrice = finalPrice / 12;
    return finalPrice;
  };

  const isMobile = useIsMobile();
  const maxBarHeight = isMobile ? 180 : 210;

  const initialTariffHeight = useMotionValue(0);
  const initialOfferHeight = useMotionValue(0);
  const finalTariffHeight = getBarHeight(tariffPrice, offerPrice, maxBarHeight);
  const finalOfferHeight = hasInvoice
    ? getBarHeight(offerPrice, tariffPrice, maxBarHeight)
    : maxBarHeight;
  const initialTariffPrice = useMotionValue(0);
  const initialOfferPrice = useMotionValue(0);
  const animationSpeed = (animationState: number) =>
    animationState == 0 ? initial : accelerated;

  useEffect(() => {
    animate(initialTariffHeight, finalTariffHeight, {
      duration: animationSpeed(initialTariffHeight.get()),
    });
    animate(initialOfferHeight, finalOfferHeight, {
      duration: animationSpeed(initialOfferHeight.get()),
    });
  }, [finalTariffHeight, finalOfferHeight]);

  useEffect(() => {
    const finalOfferPrice = calculatePrice(offerPrice);
    const finalTariffPrice = calculatePrice(tariffPrice);
    animate(initialTariffPrice, finalTariffPrice, {
      duration: animationSpeed(initialTariffPrice.get()),
    });
    animate(initialOfferPrice, finalOfferPrice, {
      duration: animationSpeed(initialOfferPrice.get()),
    });
    const tariffLength = Math.round(finalTariffPrice).toString().length;
    const offerLength = Math.round(finalOfferPrice).toString().length;
    setIncludeDecimals(tariffLength < 4 || offerLength < 4);
  }, [offerPrice, tariffPrice, isMonthlyRate, includeGridCosts]);

  return (
    <div>
      {isMobile ? (
        <div className="flex flex-col">
          <div className="mt-6 mb-3 flex flex-row gap-x-6 items-end align-center justify-center min-h-[212px]">
            {hasInvoice && (
              <div
                data-testid="tariff-price-mobile"
                className="flex flex-col items-end flex-1"
              >
                <PriceAmount
                  priceType={PriceType.tariff}
                  price={initialTariffPrice}
                  includeDecimals={includeDecimals}
                />
                {priceBar(PriceType.tariff, initialTariffHeight)}
              </div>
            )}
            <div
              data-testid="offer-price-mobile"
              className={`flex flex-col ${hasInvoice ? 'items-start' : 'items-center'} flex-1`}
            >
              <PriceAmount
                priceType={PriceType.offer}
                price={initialOfferPrice}
                includeDecimals={includeDecimals}
              />
              <div className="flex items-end">
                {priceBar(PriceType.offer, initialOfferHeight)}
              </div>
            </div>
          </div>
          <div className="flex flex-row gap-x-6">
            {hasInvoice && (
              <div
                data-testid="tariff-title-mobile"
                className="flex flex-1 justify-end"
              >
                <TariffName priceType={PriceType.tariff} />
              </div>
            )}
            <div
              data-testid="offer-title-mobile"
              className={`flex flex-1 ${hasInvoice ? 'justify-start' : 'justify-center'}`}
            >
              <TariffName priceType={PriceType.offer} />
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-[60px] mb-10 mx-6 flex flex-row items-end align-center justify-center min-h-[210px]">
          {hasInvoice && (
            <div className="flex flex-col items-end mr-8 flex-1">
              <div data-testid="tariff-title-desktop">
                <TariffName priceType={PriceType.tariff} />
              </div>
              <div data-testid="tariff-price-desktop">
                <PriceAmount
                  priceType={PriceType.tariff}
                  price={initialTariffPrice}
                  includeDecimals={includeDecimals}
                />
              </div>
            </div>
          )}
          {hasInvoice && priceBar(PriceType.tariff, initialTariffHeight, true)}
          {priceBar(PriceType.offer, initialOfferHeight)}
          <div
            className={`flex flex-col items-start ml-8 ${hasInvoice ? 'flex-1' : ''}`}
          >
            {savings != null && savings >= 12 && (
              <SavingsInfo savings={savings} isMonthlyRate={isMonthlyRate} />
            )}
            <Image
              data-testid="savings-company-logo"
              src={logo.url}
              alt={logo.title}
              height={24}
              width={133}
              className="mb-2"
            />
            <div data-testid="offer-title-desktop">
              <TariffName priceType={PriceType.offer} />
            </div>
            <div data-testid="offer-price-desktop">
              <PriceAmount
                priceType={PriceType.offer}
                price={initialOfferPrice}
                includeDecimals={includeDecimals}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
